import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, message, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { testItemService } from '../../services/testItemService';
import { TestData } from '../../types/testItem';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

interface Props {
    projectId: number;
    visible: boolean;
    onCancel: () => void;
    onSuccess: (data: Omit<TestData, 'id'> | TestData) => void;
}

interface KeyValuePair {
    key: string;
    value: string;
}

const TestDataForm: React.FC<Props> = ({
    projectId,
    visible,
    onCancel,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [keyValuePairs, setKeyValuePairs] = useState<KeyValuePair[]>([{ key: '', value: '' }]);

    useEffect(() => {
        if (visible) {
            form.resetFields();
            form.setFieldsValue({
                project_id: projectId,
            });
            setKeyValuePairs([{ key: '', value: '' }]);
        }
    }, [visible, form, projectId]);

    const handleAddPair = () => {
        setKeyValuePairs([...keyValuePairs, { key: '', value: '' }]);
    };

    const handleRemovePair = (index: number) => {
        const newPairs = keyValuePairs.filter((_, i) => i !== index);
        setKeyValuePairs(newPairs);
    };

    const handlePairChange = (index: number, field: 'key' | 'value', value: string) => {
        const newPairs = [...keyValuePairs];
        newPairs[index] = { ...newPairs[index], [field]: value };
        setKeyValuePairs(newPairs);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            // Convert key-value pairs to JSON object
            const dataObject: Record<string, any> = {};
            keyValuePairs.forEach(pair => {
                if (pair.key && pair.value) {
                    try {
                        // Try to parse value as JSON if it looks like JSON
                        dataObject[pair.key] = pair.value.startsWith('{') || pair.value.startsWith('[') 
                            ? JSON.parse(pair.value) 
                            : pair.value;
                    } catch {
                        // If parsing fails, use the value as is
                        dataObject[pair.key] = pair.value;
                    }
                }
            });

            const data = {
                name: values.name,
                description: values.description,
                data: dataObject,
                project_id: projectId,
            };

            // Передаем данные в родительский компонент для создания
            onSuccess(data);
            // Сбрасываем форму перед закрытием
            form.resetFields();
            setKeyValuePairs([{ key: '', value: '' }]);
        } catch (error) {
            console.error('Error creating test data:', error);
            message.error(t('testData.createError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={t('testData.create')}
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t('common.cancel')}
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                    {t('common.create')}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="name"
                    label={t('testData.name')}
                    rules={[{ required: true, message: t('testData.nameRequired') }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    name="description"
                    label={t('testData.description')}
                >
                    <Input.TextArea />
                </Form.Item>
                <Typography.Title level={5}>{t('testData.data')}</Typography.Title>
                {keyValuePairs.map((pair, index) => (
                    <Space key={index} style={{ display: 'flex', marginBottom: 8 }}>
                        <Form.Item
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                placeholder={t('testData.key')}
                                value={pair.key}
                                onChange={(e) => handlePairChange(index, 'key', e.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                placeholder={t('testData.value')}
                                value={pair.value}
                                onChange={(e) => handlePairChange(index, 'value', e.target.value)}
                            />
                        </Form.Item>
                        {keyValuePairs.length > 1 && (
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleRemovePair(index)}
                            />
                        )}
                    </Space>
                ))}
                <Button
                    type="dashed"
                    onClick={handleAddPair}
                    block
                    icon={<PlusOutlined />}
                >
                    {t('testData.addPair')}
                </Button>
            </Form>
        </Modal>
    );
};

export default TestDataForm; 